import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { PenLine, Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <PenLine className="w-8 h-8 text-brand-accent" />
            <span className="font-serif text-2xl font-semibold text-brand-primary">Collectively Write</span>
          </Link>
          <h1 className="font-serif text-3xl font-semibold text-text-primary mb-2">Reset your password</h1>
          <p className="text-text-secondary">Enter your email and we'll send you a reset link</p>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-soft-md border border-brand-primary/10">
          {sent ? (
            <div className="text-center py-4" data-testid="reset-email-sent">
              <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
              <h2 className="font-serif text-xl font-semibold text-text-primary mb-2">Check your inbox</h2>
              <p className="text-text-secondary mb-6">
                If an account exists for <strong>{email}</strong>, we've sent a password reset link. It expires in 1 hour.
              </p>
              <p className="text-sm text-text-muted mb-6">
                Don't see it? Check your spam folder.
              </p>
              <Link to="/login">
                <Button className="btn-primary h-11 gap-2" data-testid="back-to-login-btn">
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                    className="h-12 pl-10"
                  />
                  <Mail className="w-4 h-4 text-text-muted absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary h-12 text-base"
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>
          )}

          {!sent && (
            <p className="text-center mt-6 text-text-secondary">
              <Link to="/login" className="text-brand-accent hover:underline font-medium inline-flex items-center gap-1" data-testid="back-to-login-link">
                <ArrowLeft className="w-4 h-4" />
                Back to login
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
